import { composeCanvases, downloadBlob, downloadCSV, rowsToCSV } from "@ortex-charts/viz";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { isOrtexApp } from "@/lib/utils";

/**
 * Chart export for this app. A chart is one or more canvases in a container;
 * composing them over the theme background gives the image the user sees.
 * On the web that becomes a download; in the Capacitor app downloads are
 * inert, so the file goes to the native share sheet instead, the same way
 * every other export in the app does it.
 */
async function deliver(blob: Blob, filename: string): Promise<void> {
    if (!isOrtexApp()) {
        downloadBlob(blob, filename);
        return;
    }
    const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
    const result = await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
    try {
        await Share.share({ title: filename, url: result.uri });
    } catch (err) {
        if ((err as Error).name !== "AbortError") throw err;
    }
}

/** Every canvas under `container`, composed in DOM order over `background`, as a PNG. */
export async function exportChartPNG(container: HTMLElement, filename = "chart.png", background?: string): Promise<void> {
    const canvases = Array.from(container.querySelectorAll("canvas")).filter((c) => c.width > 0 && c.height > 0);
    if (!canvases.length) return;
    const composed = composeCanvases(canvases, background);
    const blob = await new Promise<Blob | null>((resolve) => composed.toBlob(resolve, "image/png"));
    if (blob) await deliver(blob, filename.endsWith(".png") ? filename : `${filename}.png`);
}

/** Rows to CSV and out, through the same web/app delivery. */
export async function exportRowsCSV(rows: readonly Record<string, unknown>[], filename = "chart.csv", columns?: readonly string[]): Promise<void> {
    if (!isOrtexApp()) {
        downloadCSV(rows, filename, columns);
        return;
    }
    await deliver(new Blob([rowsToCSV(rows, columns)], { type: "text/csv;charset=utf-8" }), filename.endsWith(".csv") ? filename : `${filename}.csv`);
}

/** A CSV string that some component already built. */
export async function exportCSVText(csv: string, filename = "chart.csv"): Promise<void> {
    if (!isOrtexApp()) {
        downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), filename);
        return;
    }
    await deliver(new Blob([csv], { type: "text/csv;charset=utf-8" }), filename);
}
